import { useState, useRef } from 'react'
import { Search, X } from 'lucide-react'

const BORDER_MAIN = '0.5px solid #4B5563'
const COLOR_ICON = '#9CA3AF'

type SearchInputSize = 'large' | 'medium' | 'small'

interface SearchInputProps {
  size?: SearchInputSize
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
}

const sizeConfig: Record<SearchInputSize, {
  height: number
  paddingX: number
  gap: number
  fontSize: number
  iconSize: number
}> = {
  large:  { height: 40, paddingX: 11, gap: 16, fontSize: 12, iconSize: 16 },
  medium: { height: 32, paddingX: 10, gap: 10, fontSize: 12, iconSize: 14 },
  small:  { height: 24, paddingX:  8, gap:  8, fontSize: 10, iconSize: 12 },
}

export function SearchInput({
  size = 'large',
  placeholder = 'Search…',
  value: controlledValue,
  onChange,
  className = '',
}: SearchInputProps) {
  const [internalQuery, setInternalQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const isControlled = controlledValue !== undefined
  const query = isControlled ? controlledValue : internalQuery

  function handleChange(val: string) {
    if (!isControlled) setInternalQuery(val)
    onChange?.(val)
  }

  const { height, paddingX, gap, fontSize, iconSize } = sizeConfig[size]

  return (
    <div
      className={`flex items-center cursor-text flex-shrink-0 ${className}`}
      style={{
        height,
        paddingLeft: paddingX,
        paddingRight: paddingX,
        gap,
        border: BORDER_MAIN,
        // Focus lifts the fill rather than recolouring the border, as stears.co does.
        backgroundColor: focused ? '#2D3036' : '#1F2937',
        transition: 'background-color 0.15s ease',
      }}
      onClick={() => inputRef.current?.focus()}
    >
      <Search size={iconSize} color={COLOR_ICON} className="flex-shrink-0" />
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-transparent font-jakarta font-normal text-text-input placeholder:text-text-support outline-none"
        style={{ fontSize, border: 'none', caretColor: '#FFFFFF', letterSpacing: '-0.12px' }}
      />
      {query && (
        <button
          className="flex-shrink-0 flex items-center justify-center hover:opacity-70 transition-opacity"
          onClick={(e) => {
            e.stopPropagation()
            handleChange('')
            inputRef.current?.focus()
          }}
        >
          <X size={iconSize} color={COLOR_ICON} />
        </button>
      )}
    </div>
  )
}
